// Interface for uploader options
interface Options {
  limit?: number;
  accept?: string;
  minSize?: number;
  maxSize?: number;
  autoUpload?: boolean;
}

// Extended interface with required limit
interface UploaderOption extends Options {
  limit: number;
}

// Type for upload item status
export type UploadItem = {
  idx: string;
  url: string;
  status: "pending" | "done" | "error";
};

// Event map for uploader events
export type UploaderEventMap = {
  start: (data: UploadItem[]) => void;
  progress: (data: UploadItem[]) => void;
  end: (data: UploadItem[]) => void;
};

// Type for event handlers
type UploaderEventMapHandle = {
  [K in keyof UploaderEventMap]: UploaderEventMap[K][];
};

// Type for upload server function
export type UploaderServer = (file: File) => Promise<string>;

// Main Uploader class
export class Uploader {
  private options: UploaderOption;
  private el: HTMLInputElement;
  private uploadServer: UploaderServer;
  private handler: UploaderEventMapHandle = {
    start: [],
    progress: [],
    end: [],
  };

  constructor(uploadServer: UploaderServer, options: Options) {
    this.options = {
      limit: 1,
      autoUpload: true,
      ...options,
    };
    this.uploadServer = uploadServer;
    this.el = this.createInput();
  }

  // Create and configure the file input element
  private createInput() {
    if (typeof document === "undefined") return {} as any;
    // Remove any existing uploader inputs
    Array.from(document.querySelectorAll(".uploader-form-input")).forEach(
      (el) => {
        el && document.body.removeChild(el);
      }
    );
    const el = document.createElement("input");
    el.className = "uploader-form-input";
    el.type = "file";
    el.style.display = "block";
    el.style.opacity = "0";
    el.style.width = "0";
    el.style.height = "0";
    el.style.position = "absolute";
    el.style.top = "0";
    el.style.left = "0";
    el.style.overflow = "hidden";
    el.multiple = this.options.limit > 1;
    if (this.options.accept) {
      el.accept = this.options.accept;
    }
    return el;
  }

  // Upload multiple files
  public async uploadFiles(files: File[]) {
    const results = files.map((file) => ({ file }));
    const uniqueId = Math.random().toString(36).substr(2, 9);
    const uploadList: UploadItem[] = results.map((item) => ({
      url: "",
      status: "pending",
      idx: `uploader-${uniqueId}`,
    }));

    // Trigger start event
    this.handler.start.map((fn) => fn(uploadList));

    await PromiseEach(
      results.map(async (file, index) => {
        try {
          const url = await this.uploadFile(file);
          uploadList[index].url = url;
          uploadList[index].status = "done";
        } catch (error) {
          uploadList[index].status = "error";
        } finally {
          // Trigger progress event
          this.handler.progress.map((fn) => fn(uploadList));
        }
      })
    );

    // Trigger end event
    this.handler.end.map((fn) => fn(uploadList));
  }

  // Upload a single file
  private async uploadFile(result: { file: File }) {
    return this.uploadServer(result.file);
  }

  // Check files for validity
  private checkFile(files: File[]) {
    const typeError = this.checkTypes(files);
    if (typeError) {
      throw new Error(typeError);
    }

    const sizeError = this.checkSize(files);
    if (sizeError) {
      throw new Error(sizeError);
    }
  }

  // Check file types
  private checkTypes(files: File[]) {
    const accept = this.options.accept;
    if (accept) {
      let fileType = "";
      if (accept.indexOf("image") !== -1) {
        fileType = "image";
      } else if (accept.indexOf("video") !== -1) {
        fileType = "video";
      }
      for (const file of files) {
        if (file.type.indexOf(fileType) !== 0) {
          return "Upload file type error!";
        }
      }
    }
    return null;
  }

  // Check file sizes
  private checkSize(files: File[]) {
    const options = this.options;
    for (const file of files) {
      if (options.minSize && file.size < options.minSize) {
        return `Upload file cannot be smaller than ${options.minSize}`;
      }
      if (options.maxSize && file.size > options.maxSize) {
        return `Upload file cannot be larger than ${options.maxSize}`;
      }
    }
    return null;
  }

  // Trigger file selection dialog
  public chooseFile() {
    return new Promise<File[]>((resolve) => {
      const el = this.el;
      document.body.appendChild(el);
      el.click();

      el.onchange = async (e: any) => {
        let files = e.target.files || [];
        files = Array.prototype.slice.call(files);
        if (files.length === 0) {
          return;
        }
        this.checkFile(files);
        if (this.options.autoUpload) {
          this.uploadFiles(files);
        }
        el.onchange = null;
        el.parentNode && el.parentNode.removeChild(el);
        resolve(files);
      };
    });
  }

  // Add event listener
  public on<K extends keyof UploaderEventMap>(
    event: K,
    fn: UploaderEventMap[K]
  ) {
    const handler = this.handler[event] as UploaderEventMap[K][];
    handler.push(fn);
  }

  // Remove event listener
  public off<K extends keyof UploaderEventMap>(
    event: K,
    fn: UploaderEventMap[K]
  ) {
    const handles = this.handler[event] as UploaderEventMap[K][];
    this.handler[event] = handles.filter(
      (item) => item !== fn
    ) as UploaderEventMapHandle[K];
  }
}

// Utility function to handle multiple promises
function PromiseEach(promiseLikes: PromiseLike<any>[]) {
  const datas: Array<any> = [];
  let count = 0;
  return new Promise((resolve) => {
    promiseLikes.forEach(async (promiseLike) => {
      try {
        const data = await promiseLike;
        datas.push(data);
      } catch (error) {
        datas.push(error);
      } finally {
        count++;
        if (count === promiseLikes.length) {
          resolve(true);
        }
      }
    });
  });
}
